/* eslint-disable lingui/no-unlocalized-strings */
import { useNavigate } from 'react-router';
import { useEffect, useRef } from 'react';
import { Rocket, Puzzle, PartyPopper, GraduationCap, User, Rabbit } from 'lucide-react';
import { useRudderStackAnalytics } from '../hooks/use-analytics';

export function LandingPage() {
  const navigate = useNavigate();

  // Refs for the sections to observe to create a parallax effect
  // assign these to the corresponding sections in the JSX
  const introRef = useRef<HTMLDivElement | null>(null);
  const worksRef = useRef<HTMLDivElement | null>(null);
  const powerRef = useRef<HTMLDivElement | null>(null);
  const badgesRef = useRef<HTMLDivElement | null>(null);
  const teamsRef = useRef<HTMLDivElement | null>(null);

  // Track page view
  const analytics = useRudderStackAnalytics();
  useEffect(() => {
    if (analytics) {
      analytics?.page(
        'Landing',
        {
          domain: 'prof.ai',
        },
        {
          page: {
            domain: 'prof.ai',
          },
        },
      );
    }
  }, [analytics]);

  const trackCta = (cta: string) => {
    analytics?.track('Landing CTA', {
      app: 'ProfAI',
      cta,
    });
  };

  // turn on the parallax effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-slide-up');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      },
    );
    if (introRef.current) observer.observe(introRef.current);
    if (worksRef.current) observer.observe(worksRef.current);
    if (powerRef.current) observer.observe(powerRef.current);
    if (badgesRef.current) observer.observe(badgesRef.current);
    if (teamsRef.current) observer.observe(teamsRef.current);

    // turn on the parallax effect
    return () => observer.disconnect();
  }, []);
  return (
    <div className="landing-page">
      <div className="landing-content-section">
        <div className="landing-hero-right">
          <img
            className="landing-hero-placeholder-img"
            alt={'ProfAI Login'}
            src="/img/landingPage/hero.png"
            width={'90%'}
            style={{ borderRadius: '10px', float: 'left' }}
          />
        </div>
        <div className="landing-hero-left">
          <div className="landing-hero-title">
            The <span style={{ color: 'var(--colors-tomato)' }}>fast,</span>{' '}
            <span style={{ color: 'var(--colors-pumpkin)' }}>fun,</span>{' '}
            <span style={{ color: 'var(--colors-cornflower)' }}>free</span> way to get AI-certified
          </div>
          <div className="landing-hero-btn-group">
            <button
              className="landing-default-btn"
              onClick={() => {
                trackCta('Get started');
                navigate('/sign-up');
              }}
            >
              Get started
            </button>
            <button
              className="landing-secondary-btn"
              onClick={() => {
                trackCta('I already have an account');
                navigate('/login');
              }}
            >
              I already have an account
            </button>
            <button
              className="landing-corp-btn"
              onClick={() => {
                trackCta('Did your company give you access to ProfAI?');
                navigate('/section');
              }}
            >
              Did your company give you access to ProfAI?{' '}
              <span className="landing-corp-btn-end">Click here</span>
            </button>
          </div>
        </div>
      </div>
      <div className={'landing-content-section landing-value parallax-section'} ref={introRef}>
        <img
          className="landing-value-img"
          alt={'ProfAI Avatar'}
          src="/img/landingPage/avatar1.svg"
        />
        <div className="landing-value-text">
          <div className="landing-value-title">The fastest, most effective way to learn AI</div>
          <div className={'landing-value-description landing-mobile-invisible'}>
            AI is a once-in-a-generation technological shift… and mastering it is a prerequisite for
            getting ahead. But figuring out what to use ChatGPT or Copilot for isn’t easy. ProfAI is
            the lightning-fast, hyper-personalized way to master prompting and find great AI use
            cases, so you can start doing more exciting work right away.
          </div>
        </div>
        <div className={'landing-value-description landing-mobile-only-visible'}>
          AI is a once-in-a-generation technological shift… and mastering it is a prerequisite for
          getting ahead. But figuring out what to use AI for isn’t easy. ProfAI is the
          lightning-fast, hyper-personalized way to master AI, so you can start doing more exciting
          work right away.
        </div>
      </div>
      <div className={'landing-content-section landing-works parallax-section'} ref={worksRef}>
        <div className="landing-section-title-group">
          <div className="landing-section-title">How ProfAI works</div>
          <div className="landing-section-title-sub">
            Personalized AI training designed to fit your job and time
          </div>
        </div>
        <div className={'landing-works-tile landing-tile-1'}>
          <div className="landing-works-tile-main">
            <div className="landing-works-tile-img">
              <User size={40} color="var(--colors-cornflower)" strokeWidth={1.5} />
            </div>
          </div>
          <div className="landing-works-tile-title">Tailored to you</div>
          <div className="landing-works-tile-description">
            Tell ProfAI what you do - it builds custom prompting exercises and identifies use cases
            just for you.
          </div>
        </div>
        <div className={'landing-works-tile landing-tile-2'}>
          <div className="landing-works-tile-main">
            <div className="landing-works-tile-img">
              <Puzzle size={40} color="black" strokeWidth={1.5} />
            </div>
          </div>
          <div className="landing-works-tile-title">Learn by doing</div>
          <div className="landing-works-tile-description">
            Practice prompting and finding an AI use case with 10+ short, interactive exercises
            designed for real-world tasks.
          </div>
        </div>
        <div className={'landing-works-tile landing-tile-3'}>
          <div className="landing-works-tile-main">
            <div className="landing-works-tile-img">
              <Rabbit size={40} color="black" strokeWidth={1.5} />
            </div>
          </div>
          <div className="landing-works-tile-title">Get certified fast</div>
          <div className="landing-works-tile-description">
            Master prompting, responsible use, and finding an AI use case... and earn your Basic AI
            Proficiency certificate in under 1 hour.
          </div>
        </div>
      </div>
      <div className="landing-power-learning parallax-section" ref={powerRef}>
        <div className="landing-content-section">
          <div className="landing-section-title-group">
            <div className="landing-section-title">Fast, personalized, and built for real work</div>
            <div className="landing-section-title-sub">
              You won&apos;t look at AI training the same way again.
            </div>
          </div>
          <div className="landing-power-content-groups">
            <div className="landing-power-content-row">
              <div className="landing-works-tile-img">
                <Rocket size={40} color="black" strokeWidth={1.5} />
              </div>
              <div className="landing-power-content-text">
                <div className="landing-power-content-title">100% personalized</div>
                <div className="landing-power-content-description">
                  Every lesson is tailored to you — no generic content.
                </div>
              </div>
            </div>
            <div className="landing-power-content-row">
              <div className="landing-works-tile-img">
                <PartyPopper size={40} color="black" strokeWidth={1.5} />
              </div>
              <div className="landing-power-content-text">
                <div className="landing-power-content-title">Practice real AI skills</div>
                <div className="landing-power-content-description">
                  Practice prompting and finding an AI use case with 10+ short, interactive
                  exercises designed for real-world tasks.
                </div>
              </div>
            </div>
            <div className="landing-power-content-row">
              <div className="landing-works-tile-img">
                <GraduationCap size={40} color="black" strokeWidth={1.5} />
              </div>
              <div className="landing-power-content-text">
                <div className="landing-power-content-title">Certified in under 1 hour</div>
                <div className="landing-power-content-description">
                  Walk away with your Basic AI Proficiency Certificate in just 60 minutes.
                </div>
              </div>
            </div>
          </div>
          <img
            className="landing-power-center-img"
            alt={'ProfAI Placeholder'}
            src="/img/landingPage/powerCenter.svg"
          />
          <img
            className={'landing-power-right-img landing-mobile-invisible'}
            alt={'ProfAI Avatar'}
            src="/img/landingPage/powerRight.svg"
          />
        </div>
      </div>
      <div className="landing-badges parallax-section" ref={badgesRef}>
        <div className="landing-content-section-sm">
          <img
            className="landing-badges-img"
            alt={'ProfAI Badges'}
            src="/img/landingPage/basicAIProficiencyBadge.svg"
          />
          <div className="landing-badges-text">
            <div className="landing-badges-title">Earn your AI certification badge in 1 hour</div>
            <div className="landing-badges-description">
              Most users walk away with a LinkedIn badge to share with their network or employer in
              less than 60 minutes.
            </div>
            <button
              className={'landing-default-btn landing-mobile-center-btn'}
              onClick={() => {
                trackCta('Get certified');
                navigate('/sign-up');
              }}
            >
              Get certified
            </button>
          </div>
        </div>
      </div>
      <div className="landing-teams parallax-section" ref={teamsRef}>
        <div className="landing-content-section-sm">
          <div className={'landing-section-title-group landing-mobile-text-center'}>
            <div className="landing-section-title landing-title-alt">
              Want ProfAI access for your whole team?
            </div>
            <div className="landing-section-title-sub landing-title-alt">
              We can customize ProfAI to your organization&apos;s policies and priorities.
            </div>
            <a
              className={'landing-secondary-btn landing-mobile-secondary-btn'}
              href="https://www.sectionai.com/ai/profai#live-demo"
              target="_blank"
              rel="noreferrer"
              style={{ float: 'none', display: 'inline-block' }}
              onClick={() => {
                trackCta('Book a demo');
              }}
            >
              Book a demo
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
